import { fetchApi } from '@/lib/api';
import type { TaskSearchHit } from '@shared/types/tasks';

type ResultsEnvelope<T> = { results: T[] };

async function searchResults<T>(
  path: string,
  q: string,
  limit: number,
  suffix = '',
  { throwOnError = false }: { throwOnError?: boolean } = {}
): Promise<T[]> {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const res = await fetchApi<ResultsEnvelope<T>>(
    `${path}?q=${encodeURIComponent(trimmed)}&limit=${limit}${suffix}`
  );
  if (res.error && throwOnError) throw new Error(res.error);
  return res.data?.results ?? [];
}

export type SavedFilterSearchItem = {
  id: string;
  pathname: string;
  name: string;
  queryString: string;
  /** The caller's own view on a page outside projects (the master board, T-26). */
  personal?: boolean;
};

/**
 * Saved views named like `q`: the current project's (`inProject`), then the caller's own on pages
 * outside projects (the master board's), at most `limit` in all.
 */
export async function searchSavedFilters(q: string, limit: number, { inProject }: { inProject: boolean }) {
  const [shared, mine] = await Promise.all([
    inProject ? searchResults<SavedFilterSearchItem>('/saved-filters/search', q, limit) : [],
    searchResults<SavedFilterSearchItem>('/my-saved-filters/search', q, limit),
  ]);
  return [...shared, ...mine.map((f) => ({ ...f, personal: true }))].slice(0, limit);
}

export function searchTasks(q: string, limit: number, options: { throwOnError?: boolean } = {}) {
  return searchResults<TaskSearchHit>('/tasks/search', q, limit, '', options);
}
